import { db } from './firebase';
import { doc, getDoc, updateDoc, serverTimestamp, collection, addDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Role constants
export const ROLES = {
  ADMIN: 'admin',
  HANDICAPPER: 'handicapper',
  USER: 'user'
};

// Role hierarchy for permission checks (higher number = more permissions)
export const ROLE_HIERARCHY = {
  admin: 3,
  handicapper: 2,
  user: 1
};

/**
 * Get the role of a user from their custom claims (preferred) or Firestore document (fallback)
 * @param {string} userId - The user's UID
 * @returns {Promise<string|null>} The user's role or null if not found
 */
export const getUserRole = async (userId) => {
  try {
    // First, try to get role from Firebase Auth custom claims
    const auth = getAuth();
    if (auth.currentUser && auth.currentUser.uid === userId) {
      const idTokenResult = await auth.currentUser.getIdTokenResult();
      if (idTokenResult.claims.role) {
        return idTokenResult.claims.role;
      }
    }
    
    // Fallback to Firestore document
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      console.warn(`User document not found for userId: ${userId}`);
      return null;
    }
    
    const userData = userDoc.data();
    // Default to 'user' if role field doesn't exist (for backward compatibility)
    return userData.role || ROLES.USER;
  } catch (error) {
    console.error('Error getting user role:', error);
    throw error;
  }
};

/**
 * Check if a user has at least the required role level
 * Uses role hierarchy: admin > handicapper > user
 * @param {string} userId - The user's UID
 * @param {string} requiredRole - The minimum required role
 * @returns {Promise<boolean>} True if user has sufficient permissions
 */
export const hasRole = async (userId, requiredRole) => {
  try {
    const userRole = await getUserRole(userId);
    
    if (!userRole) {
      return false;
    }
    
    const userLevel = ROLE_HIERARCHY[userRole] || 0;
    const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
    
    return userLevel >= requiredLevel;
  } catch (error) {
    console.error('Error checking user role:', error);
    return false;
  }
};

/**
 * Update a user's role (admin only)
 * Logs the change to adminLogs collection
 * @param {string} targetUserId - The user whose role is being changed
 * @param {string} newRole - The new role to assign
 * @param {string} performedBy - The UID of the admin performing the action
 * @param {string} oldRole - The previous role (optional, will be fetched if not provided)
 * @returns {Promise<void>}
 */
export const updateUserRole = async (targetUserId, newRole, performedBy, oldRole = null) => {
  try {
    // Verify the new role is valid
    if (!Object.values(ROLES).includes(newRole)) {
      throw new Error(`Invalid role: ${newRole}`);
    }
    
    // Get old role if not provided
    if (!oldRole) {
      oldRole = await getUserRole(targetUserId);
    }
    
    // Update the user's role
    const userRef = doc(db, 'users', targetUserId);
    await updateDoc(userRef, {
      role: newRole,
      roleAssignedAt: serverTimestamp(),
      roleAssignedBy: performedBy
    });
    
    // Log the action to adminLogs
    await addDoc(collection(db, 'adminLogs'), {
      action: 'role_changed',
      targetUserId,
      performedBy,
      timestamp: serverTimestamp(),
      oldRole: oldRole || 'unknown',
      newRole,
      details: `Role changed from ${oldRole || 'unknown'} to ${newRole}`
    });
    
    console.log(`Role updated: User ${targetUserId} changed from ${oldRole} to ${newRole} by ${performedBy}`);
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

/**
 * Check if a user is an admin
 * @param {Object} user - User object with role property
 * @returns {boolean}
 */
export const isAdmin = (user) => {
  return user?.role === ROLES.ADMIN;
};

/**
 * Check if a user is a handicapper
 * @param {Object} user - User object with role property
 * @returns {boolean}
 */
export const isHandicapper = (user) => {
  return user?.role === ROLES.HANDICAPPER;
};

/**
 * Check if a user is a regular user
 * @param {Object} user - User object with role property
 * @returns {boolean}
 */
export const isUser = (user) => {
  return user?.role === ROLES.USER;
};

/**
 * Check if a user has admin or handicapper role
 * @param {Object} user - User object with role property
 * @returns {boolean}
 */
export const isHandicapperOrAdmin = (user) => {
  return isAdmin(user) || isHandicapper(user);
};

/**
 * Get a user-friendly display name for a role
 * @param {string} role - The role constant
 * @returns {string}
 */
export const getRoleDisplayName = (role) => {
  const displayNames = {
    [ROLES.ADMIN]: 'Admin',
    [ROLES.HANDICAPPER]: 'Handicapper',
    [ROLES.USER]: 'User'
  };
  
  return displayNames[role] || 'Unknown';
};
